/**
 * Small page-level primitives shared by every route: a header, an error banner,
 * and an empty state. Keeping them here avoids re-styling the same chrome per
 * page and keeps spacing/typography consistent.
 */
import type { ReactNode } from "react";
import { ApiError } from "@/lib/api/client";

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    // md-enter: the header fades/rises in on mount so a route change reads as the page arriving
    // rather than repainting. One-shot, reduced-motion-safe (see global.css).
    <div className="md-enter mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        {/* A short brand tick before the title — the one spot the brand accent earns a place in
            page chrome: it marks "you are here" without spending a status colour. Tracking is
            tightened a hair for a more composed masthead. */}
        <h1 className="flex items-center gap-2.5 text-2xl font-semibold tracking-[-0.01em] text-ink">
          <span aria-hidden className="h-5 w-1 shrink-0 rounded-full bg-brand" />
          {title}
        </h1>
        {subtitle && <p className="mt-1 pl-[0.875rem] text-sm text-ink-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Uniform error surface. Distinguishes a transport failure (backend down /
 *  offline) from an API error so the operator knows which to chase. */
export function ErrorBanner({ error }: { error: unknown }) {
  const transport = error instanceof ApiError && error.isTransport;
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div
      role="alert"
      className="md-card border-critical/40 bg-critical-soft p-4 text-sm text-critical"
    >
      <div className="font-medium">
        {transport ? "Controller unreachable" : "Request failed"}
      </div>
      <div className="mt-1 text-critical/90">{message}</div>
    </div>
  );
}

/**
 * EmptyState — the "nothing here (yet)" surface.
 *
 * An empty list is a moment, not an error: it gets a centred glyph in a soft well, a title, an
 * optional hint, and room for an optional action (e.g. a "Clear filters" / "Add" button). The
 * icon sits in a surface-2 disc rather than carrying a status or brand colour, because an empty
 * list is neither unhealthy nor an affordance. A restrained default glyph ships so every call
 * site reads as intentional even when it passes none.
 */
export function EmptyState({
  title,
  hint,
  icon,
  action,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="md-card md-enter flex flex-col items-center px-6 py-12 text-center">
      <div className="mb-4 grid h-14 w-14 place-items-center rounded-full bg-surface-2 text-ink-subtle">
        {icon ?? <DefaultEmptyIcon />}
      </div>
      <div className="text-base font-medium text-ink">{title}</div>
      {hint && <div className="mt-1 max-w-sm text-sm text-ink-muted">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Default empty glyph: a stroked tray/inbox outline. Neutral, currentColor so it tracks the
 *  muted ink of the disc it sits in (and both themes). */
function DefaultEmptyIcon() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 24 24"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 13h4l2 3h6l2-3h4" />
      <path d="M5 13V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7" />
    </svg>
  );
}
